import React, { useCallback, useState } from 'react';
import {
  MODULE_LIMITS,
  STORY_COACH_PROMPTS,
  generateStoryCoachAnswersFromBrief,
  getStoryCoachCreativeRecommendations,
} from '../../services/giftBuilderConfig';

const StoryCoachStep = ({
  pageData,
  theme,
  onStoryCoachAnswer,
  onApplyStoryCoach,
  onApplyAllStoryCoach,
  onAddModule,
}) => {
  const [storyBrief, setStoryBrief] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const applyStoryBrief = useCallback(() => {
    const answers = generateStoryCoachAnswersFromBrief(storyBrief, pageData);
    Object.entries(answers).forEach(([key, value]) => {
      if (value) onStoryCoachAnswer(key, value);
    });
  }, [onStoryCoachAnswer, pageData, storyBrief]);

  const modules = Array.isArray(pageData.modules) ? pageData.modules : [];
  const creativeRecommendations = getStoryCoachCreativeRecommendations(pageData);
  const answeredCount = STORY_COACH_PROMPTS.filter((item) => (
    pageData.storyCoachAnswers?.[item.key] || ''
  ).trim()).length;

  return (
    <section id="gift-builder-coach" className="scroll-mt-6 lumirae-editor-panel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
            Content Helper / Story Coach
          </div>
          <p className="mt-2 text-xs font-medium leading-relaxed opacity-60">
            {answeredCount > 0
              ? `${answeredCount}/${STORY_COACH_PROMPTS.length} prompts answered. Apply them when the story feels ready.`
              : 'Optional helper. Keep it closed until you want help writing the gift.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="lumirae-ds-button lumirae-ds-button-secondary px-4 py-3"
            aria-expanded={isExpanded}
          >
            {isExpanded ? 'Hide Coach' : 'Open Coach'}
          </button>
          <button
            type="button"
            onClick={onApplyAllStoryCoach}
            className="lumirae-ds-button lumirae-ds-button-primary px-5 py-3"
          >
            Apply Suggestions
          </button>
        </div>
      </div>
      {isExpanded && (
        <>
          <p className="mt-3 rounded-[1.25rem] bg-white/60 px-4 py-3 text-[9px] font-black uppercase leading-relaxed tracking-widest opacity-55">
            Uses your answers to update the letter, photo caption, voice prompt, timeline, and final card.
          </p>
          <div className="mt-4 rounded-[1.5rem] border bg-white/55 p-4" style={{ borderColor: 'var(--lumirae-border-default)' }}>
            <label className="lumirae-ds-field-label">Start from one sentence</label>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <input
                type="text"
                value={storyBrief}
                maxLength={120}
                onChange={(event) => setStoryBrief(event.target.value)}
                className="lumirae-ds-input min-w-0 px-4 py-3 text-[11px] font-normal"
                placeholder="Example: For my mom's 50th birthday"
              />
              <button
                type="button"
                onClick={applyStoryBrief}
                disabled={!storyBrief.trim()}
                className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-3 disabled:opacity-40"
              >
                Suggest Story
              </button>
            </div>
          </div>
          {creativeRecommendations.length > 0 && (
            <div className="mt-4 rounded-[1.5rem] border bg-white/55 p-4" style={{ borderColor: 'var(--lumirae-border-default)' }}>
              <div className="text-[9px] font-black uppercase tracking-[0.24em]" style={{ color: theme.accent }}>
                Recommended creative moment
              </div>
              <p className="mt-2 text-xs font-medium leading-relaxed opacity-65">
                Story Coach found a stronger module for this gift type. Add it only if it supports the story.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3">
                {creativeRecommendations.map((recommendation) => {
                  const sameTypeCount = modules.filter((module) => module.type === recommendation.moduleType).length;
                  const maxInstances = MODULE_LIMITS[recommendation.moduleType] || 2;
                  const limitReached = sameTypeCount >= maxInstances;
                  return (
                    <div key={recommendation.moduleType} className="lumirae-ds-list-card p-4">
                      <div className="grid gap-3">
                        <div className="min-w-0">
                          <div className="text-xs font-black uppercase tracking-[0.18em]" style={{ color: 'var(--lumirae-text-primary)' }}>
                            {recommendation.label}
                          </div>
                          <p className="mt-2 text-xs font-medium leading-relaxed opacity-65">
                            {recommendation.reason}
                          </p>
                          <p className="mt-2 text-[10px] font-black uppercase leading-relaxed tracking-[0.12em] opacity-45">
                            {recommendation.prompt}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => onAddModule(recommendation.moduleType)}
                          disabled={recommendation.alreadyAdded || limitReached}
                          className="lumirae-ds-button lumirae-ds-button-secondary w-full px-4 py-3 disabled:opacity-35"
                        >
                          {recommendation.alreadyAdded ? 'Added' : 'Add Moment'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-4 grid grid-cols-1 gap-3">
            {STORY_COACH_PROMPTS.map((item) => {
              const hasModule = modules.some((module) => module.type === item.suggestedModule);
              const answer = pageData.storyCoachAnswers?.[item.key] || '';
              return (
                <div key={item.title} className="lumirae-ds-list-card p-4">
                  <div className="text-[9px] font-black uppercase tracking-[0.24em]" style={{ color: theme.accent }}>
                    {item.title}
                  </div>
                  <p className="mt-2 text-xs font-medium leading-relaxed opacity-65">
                    {item.prompt}
                  </p>
                  <textarea
                    maxLength={180}
                    value={answer}
                    onChange={(event) => onStoryCoachAnswer(item.key, event.target.value)}
                    placeholder={item.placeholder}
                    className="lumirae-ds-input mt-3 min-h-[144px] resize-none overflow-hidden p-4 text-sm font-normal leading-relaxed"
                  />
                  <div className="mt-2 text-right text-[8px] font-black uppercase tracking-widest opacity-35">
                    {answer.length}/180
                  </div>
                  <button
                    type="button"
                    onClick={() => answer.trim() ? onApplyStoryCoach(item) : onAddModule(item.suggestedModule)}
                    disabled={!answer.trim() && hasModule && (MODULE_LIMITS[item.suggestedModule] || 2) === 1}
                    className="lumirae-ds-button lumirae-ds-button-secondary mt-3 px-4 py-2 disabled:opacity-35"
                  >
                    {answer.trim() ? 'Apply to Story' : (hasModule ? 'Already Started' : item.action)}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
};

export default StoryCoachStep;
